import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import {
  Modal,
  Portal,
  Text,
  Button,
  List,
  useTheme,
  ActivityIndicator,
  HelperText,
  Checkbox,
} from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useCompanyContext } from '../context/CompanyContext';
import { getOwnedCompanies, OwnedCompanySummary } from '../api/apiService';
import { SCREEN_WIDTH } from '../lib/constants';

interface TransferModalProps {
  visible: boolean;
  onDismiss: () => void;
  onTransfer: (targetCompanyId: number, transferRelated?: boolean) => Promise<void>;
  title: string;
  description: string;
  currentCompanyId?: number;
  transferRelatedVehicles?: boolean; // Show option to transfer related vehicles
  transferRelatedDrivers?: boolean; // Show option to transfer related drivers
  relatedItemsCount?: number; // Count of related items
  itemCount?: number; // Count of items being transferred
}

export const TransferModal: React.FC<TransferModalProps> = ({
  visible,
  onDismiss,
  onTransfer,
  title,
  description,
  currentCompanyId,
  transferRelatedVehicles = false,
  transferRelatedDrivers = false,
  relatedItemsCount = 0,
  itemCount = 1,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { companies: contextCompanies } = useCompanyContext();
  const [companies, setCompanies] = useState<OwnedCompanySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [selectedTargetCompanyId, setSelectedTargetCompanyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transferRelated, setTransferRelated] = useState(false);

  useEffect(() => {
    if (visible) {
      loadCompanies();
      setTransferRelated(false); // Reset checkbox when modal opens
    }
  }, [visible]);

  const loadCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOwnedCompanies();
      if (response.ok) {
        const allCompanies = response.data || [];
        // Filter out the current company
        const availableCompanies = currentCompanyId
          ? allCompanies.filter((c) => c.id !== currentCompanyId)
          : allCompanies;
        setCompanies(availableCompanies);
        
        // If no companies available, show error
        if (availableCompanies.length === 0) {
          setError(t('transfer.noOtherCompanies', 'You need at least one other company to transfer to.'));
        }
      } else {
        setError(t('transfer.errorLoadingCompanies', 'Error loading companies'));
      }
    } catch (e: any) {
      setError(e?.message || t('transfer.errorLoadingCompanies', 'Error loading companies'));
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedTargetCompanyId) {
      setError(t('transfer.selectCompany', 'Please select a target company'));
      return;
    }

    setTransferring(true);
    setError(null);
    try {
      await onTransfer(selectedTargetCompanyId, transferRelated);
      onDismiss();
    } catch (e: any) {
      setError(e?.message || t('transfer.error', 'Error transferring'));
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[
          styles.modalContent,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <Text variant="titleLarge" style={styles.title}>
          {title}
        </Text>
        <Text variant="bodyMedium" style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
          {description}
        </Text>

        {loading ? (
          <View style={styles.centerContent}>
            <ActivityIndicator size="large" />
          </View>
        ) : error && companies.length === 0 ? (
          <HelperText type="error" style={styles.errorText}>
            {error}
          </HelperText>
        ) : (
          <>
            <List.Section>
              {companies.map((company) => (
                <List.Item
                  key={company.id}
                  title={company.name}
                  description={`${t('nipc', 'NIPC')}: ${company.nipc}`}
                  left={(props) => (
                    <List.Icon
                      {...props}
                      icon={selectedTargetCompanyId === company.id ? 'check-circle' : 'circle-outline'}
                      color={selectedTargetCompanyId === company.id ? theme.colors.primary : undefined}
                    />
                  )}
                  onPress={() => {
                    setSelectedTargetCompanyId(company.id);
                    setError(null);
                  }}
                  style={[
                    styles.listItem,
                    selectedTargetCompanyId === company.id && {
                      backgroundColor: theme.colors.primaryContainer,
                    },
                  ]}
                />
              ))}
            </List.Section>

            {/* Related items transfer option */}
            {(transferRelatedVehicles || transferRelatedDrivers) && relatedItemsCount > 0 && (
              <View style={styles.relatedItemsContainer}>
                <View style={styles.checkboxContainer}>
                  <Checkbox
                    status={transferRelated ? 'checked' : 'unchecked'}
                    onPress={() => setTransferRelated(!transferRelated)}
                  />
                  <Text
                    variant="bodyMedium"
                    style={styles.checkboxLabel}
                    onPress={() => setTransferRelated(!transferRelated)}
                  >
                    {transferRelatedVehicles
                      ? t('transfer.alsoTransferVehicles', `Also transfer {{count}} related vehicles`, { count: relatedItemsCount })
                      : t('transfer.alsoTransferDrivers', `Also transfer {{count}} related drivers`, { count: relatedItemsCount })}
                  </Text>
                </View>
              </View>
            )}

            {error && (
              <HelperText type="error" style={styles.errorText}>
                {error}
              </HelperText>
            )}

            <View style={styles.buttonContainer}>
              <Button
                mode="outlined"
                onPress={onDismiss}
                disabled={transferring}
                style={styles.button}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                mode="contained"
                onPress={handleTransfer}
                disabled={transferring || !selectedTargetCompanyId || companies.length === 0}
                loading={transferring}
                style={styles.button}
              >
                {t('transfer.transferButton', 'Transfer')}
              </Button>
            </View>
          </>
        )}
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    padding: 20,
    margin: 20,
    borderRadius: 8,
    maxHeight: '80%',
    ...(Platform.OS === 'web' ? {
      maxWidth: 600,
      width: '90%',
      alignSelf: 'center',
      ...(Platform.select({
        web: {
          maxWidth: Math.min(600, SCREEN_WIDTH * 0.9),
        },
      })),
    } : {}),
  },
  title: {
    marginBottom: 8,
    fontWeight: '600',
  },
  description: {
    marginBottom: 16,
  },
  centerContent: {
    padding: 20,
    alignItems: 'center',
  },
  listItem: {
    borderRadius: 8,
    marginVertical: 4,
  },
  errorText: {
    marginTop: 8,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    gap: 8,
  },
  button: {
    minWidth: 100,
  },
  relatedItemsContainer: {
    marginTop: 16,
    marginBottom: 8,
    padding: 12,
    backgroundColor: 'transparent',
    borderRadius: 8,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxLabel: {
    marginLeft: 8,
    flex: 1,
  },
});

